/**
 * TimeOffMenu — floating popover anchored to a dispatch cell.
 *
 * Triggered by double-click on a cell. Lists every dispatch category as a
 * CHECKBOX; whatever is already on the day comes pre-checked. Tick/untick
 * as many as needed, then Save (button or Enter) commits in one write.
 *
 * Scope toggle: when the day belongs to a multi-day off block the user
 * chooses whether newly added categories apply to just that day or across
 * the whole range (range is the default). Unticking always carves out only
 * the clicked day so the rest of the range survives.
 *
 * Categories flagged `capture_details` expose inline hours/notes inputs.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { X, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { updateTechnician } from "@/lib/api";
import { useDispatchCategories, findCategory } from "@/lib/dispatchCategories";
import { CategoryChecklist } from "@/components/CategoryChecklist";
import { NON_WO_KINDS, kindMeta } from "@/components/dispatch/JobTag";

function newId() {
  return "to_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

export default function TimeOffMenu({ tech, dateStr, anchorRect, existingBlock, dayBlocks = [], onClose, onSaved, onRequestRange, onAddJob }) {
  const ref = useRef(null);
  const categories = useDispatchCategories();
  const [saving, setSaving] = useState(false);

  const existingLabels = useMemo(() => dayBlocks.map(b => b.reason).filter(Boolean), [dayBlocks]);
  const multiDay = dayBlocks.some(b => b.start_date !== (b.end_date || b.start_date));
  const rangeStart = dayBlocks.reduce((m, b) => (!m || b.start_date < m ? b.start_date : m), null) || dateStr;
  const rangeEnd = dayBlocks.reduce((m, b) => {
    const e = b.end_date || b.start_date;
    return !m || e > m ? e : m;
  }, null) || dateStr;

  const [selected, setSelected] = useState(existingLabels);
  const [scope, setScope] = useState(multiDay ? "range" : "day");
  const [details, setDetails] = useState(() => {
    const d = {};
    dayBlocks.forEach(b => {
      if (b.reason) d[b.reason] = { hours: typeof b.hours === "number" ? b.hours : 10, notes: b.notes || "" };
    });
    return d;
  });

  const toggle = (label) => setSelected(s => (s.includes(label) ? s.filter(l => l !== label) : [...s, label]));
  const onDetailChange = (label, patch) => setDetails(d => ({ ...d, [label]: { hours: 10, notes: "", ...(d[label] || {}), ...patch } }));

  // Click-outside + Escape closes, Enter saves (except inside a textarea).
  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Enter" && !e.shiftKey && e.target?.tagName !== "TEXTAREA") {
        e.preventDefault();
        saveRef.current?.();
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const top = anchorRect ? Math.min(anchorRect.bottom + 4, window.innerHeight - 400) : 100;
  const left = anchorRect ? Math.min(anchorRect.left, window.innerWidth - 280) : 100;

  const persist = async (nextArr, msg) => {
    setSaving(true);
    try {
      await updateTechnician(tech.id, { time_off: nextArr });
      toast.success(msg);
      onSaved?.();
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
      onClose();
    }
  };

  const blockIdx = (arr, block) => {
    if (!block) return -1;
    if (block.id && !String(block.id).startsWith("legacy_")) {
      const i = arr.findIndex(t => t.id === block.id);
      if (i >= 0) return i;
    }
    return arr.findIndex(t => t.start_date === block.start_date && t.end_date === block.end_date && t.reason === block.reason);
  };

  // Drop only `dateStr` out of a block, keeping the surrounding days.
  const carveDay = (arr, block) => {
    const idx = blockIdx(arr, block);
    if (idx < 0) return;
    const blk = arr[idx];
    const keep = [];
    if (blk.start_date < dateStr) keep.push({ ...blk, id: blk.id || newId(), end_date: prevDay(dateStr) });
    if ((blk.end_date || blk.start_date) > dateStr) keep.push({ ...blk, id: newId(), start_date: nextDay(dateStr) });
    arr.splice(idx, 1, ...keep);
  };

  const handleSave = () => {
    if (saving) return;
    const arr = [...(tech?.time_off || [])];
    const start = scope === "range" ? rangeStart : dateStr;
    const end = scope === "range" ? rangeEnd : dateStr;
    const added = selected.filter(l => !existingLabels.includes(l));
    const removed = existingLabels.filter(l => !selected.includes(l));

    // 1. Detail edits on categories that stay checked.
    let detailChanged = false;
    existingLabels.filter(l => selected.includes(l)).forEach(l => {
      const cat = findCategory(categories, l);
      if (!cat?.capture_details) return;
      const blk = dayBlocks.find(b => b.reason === l);
      const idx = blockIdx(arr, blk);
      if (idx < 0) return;
      const hours = parseFloat(details[l]?.hours) || 10;
      const notes = details[l]?.notes || "";
      if (arr[idx].hours !== hours || (arr[idx].notes || "") !== notes) detailChanged = true;
      arr[idx] = { ...arr[idx], id: arr[idx].id || newId(), hours, notes };
    });

    // 2. Removals — carve out just this day.
    removed.forEach(l => carveDay(arr, dayBlocks.find(b => b.reason === l)));

    // 3. Additions.
    added.forEach(l => {
      const cat = findCategory(categories, l);
      // Dedupe: swallow any same-category block already sitting inside the
      // target range so a whole-range add doesn't double up on some days.
      for (let i = arr.length - 1; i >= 0; i--) {
        const b = arr[i];
        if (b.reason === l && b.start_date >= start && (b.end_date || b.start_date) <= end) arr.splice(i, 1);
      }
      const extras = cat?.capture_details ? { hours: parseFloat(details[l]?.hours) || 10, notes: details[l]?.notes || "" } : {};
      arr.push({ id: newId(), start_date: start, end_date: end, reason: l, category_id: cat?.id || null, ...extras });
    });

    if (!added.length && !removed.length && !detailChanged) { onClose(); return; }
    const msg = added.length && removed.length
      ? "Schedule updated"
      : added.length
        ? `Added ${added.join(", ")}`
        : removed.length
          ? `Removed ${removed.join(", ")}`
          : "Updated";
    persist(arr, msg);
  };
  const saveRef = useRef(handleSave);
  saveRef.current = handleSave;

  const clear = () => {
    if (!dayBlocks.length) { onClose(); return; }
    const arr = [...(tech?.time_off || [])];
    dayBlocks.forEach(b => carveDay(arr, b));
    persist(arr, "Cleared");
  };

  return (
    <div
      ref={ref}
      className="fixed z-50 w-64 bg-white border border-slate-200 rounded-lg shadow-xl py-1"
      style={{ top, left }}
      data-testid="time-off-menu"
    >
      {onAddJob && (
        <>
          {NON_WO_KINDS.map(k => (
            <button
              key={k}
              type="button"
              onClick={() => { onClose(); onAddJob(k); }}
              className={`w-full px-3 py-1.5 text-left text-sm font-semibold hover:bg-slate-50 ${kindMeta(k).textCls}`}
              data-testid={`sched-menu-add-${k}`}
            >
              + {kindMeta(k).label}
            </button>
          ))}
          <div className="border-t border-slate-200 mb-1" />
        </>
      )}
      <div className="px-3 pt-1 pb-1.5 text-[10px] uppercase tracking-wide text-slate-400">
        Categories — {dateStr}
      </div>

      <CategoryChecklist
        categories={categories}
        selected={selected}
        details={details}
        onToggle={toggle}
        onDetailChange={onDetailChange}
        maxHeight={250}
        testPrefix="sched-menu"
      />

      {multiDay && (
        <div className="px-3 pt-2" data-testid="sched-menu-scope">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Apply additions to</div>
          <div className="flex rounded-md border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setScope("range")}
              className={`flex-1 px-2 py-1 text-[11px] ${scope === "range" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid="sched-menu-scope-range"
            >
              Whole range
            </button>
            <button
              type="button"
              onClick={() => setScope("day")}
              className={`flex-1 px-2 py-1 text-[11px] border-l border-slate-200 ${scope === "day" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid="sched-menu-scope-day"
            >
              This day
            </button>
          </div>
          <div className="text-[10px] text-slate-400 mt-1">{rangeStart} → {rangeEnd}</div>
        </div>
      )}

      <div className="px-3 py-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="w-full h-8 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-60"
          data-testid="sched-menu-save"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <div className="text-[10px] text-slate-400 text-center mt-1">Enter to save · Esc to cancel</div>
      </div>

      <div className="border-t border-slate-200" />
      <button
        onClick={() => { onClose(); onRequestRange?.(existingBlock?.reason); }}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 text-slate-600"
        data-testid="sched-menu-range"
      >
        <CalendarRange className="w-3.5 h-3.5 shrink-0" />
        Custom range…
      </button>
      <button
        onClick={clear}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-rose-50 text-rose-600"
        data-testid="sched-menu-clear"
      >
        <X className="w-3.5 h-3.5 shrink-0" />
        Clear cell
      </button>
    </div>
  );
}

function prevDay(iso) {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}
function nextDay(iso) {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}
